import { useState } from 'react'
import { useParams } from 'react-router'
import { api } from '../../api/client'
import { Button } from '../../platform/primitives/Button'
import { emit } from '../../platform/analytics/analytics'
import { AuthCard } from './AuthCard'
import './auth.css'

type Choice = 'approve' | 'deny'

/**
 * What the screen is showing.
 *
 * **Not `AsyncState`**, and the reason is that this screen has no data. `Async`
 * covers loading / empty / stale / error / success for a surface backed by a
 * fetch; here the first state is a QUESTION nobody has answered yet, which that
 * union has no arm for, and `empty` and `stale` can never occur. CLAUDE.md
 * routes every list, chart and panel through `Async` — this is none of those.
 *
 * The two failure arms are the point of the whole type. `spent` is terminal:
 * an expired or already-used token cannot be made to work, so the screen offers
 * no retry. `unreachable` is not: the request never arrived, so pressing the
 * same button again is exactly the right move. Most apps render one "something
 * went wrong" for both, and only one of them is worth a button.
 */
type Screen =
  | { status: 'asking' }
  | { status: 'sending'; choice: Choice }
  | { status: 'done'; choice: Choice }
  | { status: 'spent' }
  | { status: 'unreachable'; choice: Choice }

/**
 * M1-R19's device approval. **Public by design.**
 *
 * The token is emailed to the account holder when a sign-in arrives from a new
 * country, and it *is* the credential — the email may open on a phone that has
 * never used the web app. So this route sits inside `BareLayout` and OUTSIDE
 * `RequireAuth`: behind the guard it would bounce to `/signin` and the security
 * email the backend already sends would lead nowhere.
 *
 * Both endpoints answer 204 with no body, so success is `response.ok` and not a
 * `data` field — an openapi-fetch call for a 204 resolves with `data`
 * undefined, which is the same shape a failure has.
 */
export function VerifyDevice() {
  const { token } = useParams()
  const [screen, setScreen] = useState<Screen>({ status: 'asking' })

  async function send(choice: Choice) {
    if (token === undefined || token === '') return
    setScreen({ status: 'sending', choice })
    try {
      const path = { token }
      const { response } =
        choice === 'approve'
          ? await api.POST('/api/v1/verify/{token}/approve', { params: { path } })
          : await api.POST('/api/v1/verify/{token}/deny', { params: { path } })

      if (!response.ok) {
        // Split on the STATUS, not on `!response.ok`. Collapsing every non-2xx
        // into `spent` defeated the distinction this whole component is built
        // around: a 502 during a deploy would have told the account holder
        // their security link was dead, on the screen that deliberately offers
        // no retry. web-architect caught it — the docstring on `Screen` argued
        // the case correctly and the code then did the thing it argues against.
        //
        // 4xx is about the TOKEN: 422 is the documented one (expired, already
        // answered, never issued), and 404/410 are the shapes the backend would
        // reach for if it ever distinguishes them. 5xx and 429 are about the
        // SERVER, and the token is still good — so they take the retry arm,
        // which already repeats the correct choice.
        const retryable = response.status >= 500 || response.status === 429
        if (retryable) {
          setScreen({ status: 'unreachable', choice })
          emit('device_approval_result', { result_status: 'unreachable' })
          return
        }
        setScreen({ status: 'spent' })
        emit('device_approval_result', { result_status: 'expired' })
        return
      }
      setScreen({ status: 'done', choice })
      emit('device_approval_result', {
        result_status: choice === 'approve' ? 'approved' : 'denied',
      })
    } catch {
      // `fetch` rejects for a network failure or a refused CORS preflight —
      // no status, so the branch above cannot see it. The token is still good,
      // which is what makes this the retryable arm.
      setScreen({ status: 'unreachable', choice })
      emit('device_approval_result', { result_status: 'unreachable' })
    }
  }

  if (screen.status === 'done') {
    return (
      <AuthCard title={screen.choice === 'approve' ? 'Sign-in approved' : 'Sign-in blocked'}>
        <p className="auth-card__note" role="status">
          {screen.choice === 'approve'
            ? 'Thanks — the sign-in has been approved. You can close this page and carry on there.'
            : "Thanks — that sign-in has been blocked. If it wasn't you, change your password from a device you trust."}
        </p>
      </AuthCard>
    )
  }

  // Decided at RENDER, not on the button press. `/verify/` with no segment does
  // not match this route, so it is defensive — but `useParams` is typed
  // possibly-undefined, and offering two buttons that can only POST to
  // `/verify/undefined/approve` asks the user to do something that cannot work.
  if (screen.status === 'spent' || token === undefined || token === '') {
    return (
      <AuthCard title="This link is no longer valid">
        {/* No retry. Retrying a spent token cannot succeed, so a button here
            would be a lie the user presses until they give up. The recovery is
            to make a new attempt, which mints a new email. */}
        <p className="auth-card__note" role="alert">
          This approval link has expired or has already been used — they're good for one sign-in
          each. Try signing in again and we'll email you a new one.
        </p>
      </AuthCard>
    )
  }

  const busy = screen.status === 'sending'

  return (
    <AuthCard title="Was this you?">
      <p className="auth-card__note">
        Someone signed in to TrackE5 from a new place. If that was you, approve it. If it wasn't,
        block it — we'll stop that sign-in.
      </p>

      {screen.status === 'unreachable' && (
        <p className="auth-card__error" role="alert">
          {/* Whose fault, and the recovery path — CLAUDE.md. The link is still
              good, which is the fact that makes the button honest. */}
          We couldn't reach TrackE5 to record that. Your link is still valid, so check your
          connection and try again.
        </p>
      )}

      {screen.status === 'unreachable' ? (
        <Button
          variant="primary"
          size="page"
          // Repeats the choice the user made. Re-sending `approve` after they
          // pressed "No" would approve a sign-in they had just refused, which
          // is the worst outcome this screen can produce.
          onClick={() => void send(screen.choice)}
        >
          Try again
        </Button>
      ) : (
        <>
          <Button
            variant="primary"
            size="page"
            disabled={busy}
            onClick={() => void send('approve')}
          >
            {busy ? 'Sending…' : 'Yes, that was me'}
          </Button>
          {/* `outline`, not a second lime surface — one per screen. The
              destructive-looking option is the SAFE one here, so it is not
              `destructive`: blocking a stranger's sign-in loses nothing. */}
          <Button variant="outline" size="page" disabled={busy} onClick={() => void send('deny')}>
            {/* States its reason while disabled, like every other control in
                this module. A control that greys out and keeps its resting
                label reads as broken — and here the reason is that the OTHER
                answer is already on its way. */}
            {busy ? 'Sending the other answer…' : 'No, block it'}
          </Button>
        </>
      )}
    </AuthCard>
  )
}
